import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

interface CarouselArrowButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    direction: 'prev' | 'next';
}

/**
 * CarouselArrowButton — the shared circular chevron control for horizontally
 * scrollable rows/carousels. Extracted from the LandingPage carousel arrows so
 * the blog CategoryPillNav and the gym-profile photo gallery reuse one styling
 * (44px touch target, brand-primary focus ring, dimmed chevron when disabled).
 *
 * Purely presentational: the parent owns the scroll ref, the `disabled` state
 * (typically "already at this end"), and the onClick scroll handler.
 */
export const CarouselArrowButton = React.forwardRef<HTMLButtonElement, CarouselArrowButtonProps>(
    ({ direction, className, ...props }, ref) => {
        const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
        return (
            <button
                ref={ref}
                type="button"
                className={cn(
                    'flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full border border-border-default bg-surface-base text-text-primary transition-colors group hover:bg-border-default disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
                    className,
                )}
                {...props}
            >
                <Icon size={22} aria-hidden="true" className="group-disabled:opacity-40" />
            </button>
        );
    },
);
CarouselArrowButton.displayName = 'CarouselArrowButton';

export default CarouselArrowButton;
